import React from 'react';
import styles from './LevelBadge.module.css';

/**
 * LevelBadge — filled-background pill for "level" indicators.
 *
 * Distinct from `StatusPill` (dot + label on a neutral wash). Use this
 * for severity / impact / criticality / rating-type fields where the
 * cell itself reads as a swatch: Critical / High / Normal / Low / None
 * (or ISO 21434 impact: Negligible / Moderate / Major / Severe).
 */
export interface LevelBadgeProps {
  /** Background CSS color (hex / token var / rgb()). Falls back to the
   *  neutral status token when omitted. */
  color?: string;
  /** Optional text-color override. When omitted the component auto-picks
   *  black/white based on the background's relative luminance (WCAG-style). */
  textColor?: string;
  /** Visible text. */
  label: React.ReactNode;
  /** 'sm' is for dense table rows. */
  size?: 'sm' | 'md';
  /** Optional wrapper class for one-off tweaks. */
  className?: string;
}

/** Pick a contrasting text color (near-black or white) for a given
 *  background hex. Uses the WCAG-relative luminance formula. Returns
 *  '#fff' when the background is not a hex string (CSS var, rgb()) so
 *  the consumer can override via `textColor` if needed. */
function getContrastingTextColor(bg?: string): string {
  if (typeof bg !== 'string' || !bg.startsWith('#')) return '#fff';
  const hex = bg.replace('#', '');
  if (hex.length !== 3 && hex.length !== 6) return '#fff';
  const full = hex.length === 3
    ? hex.split('').map((c) => c + c).join('')
    : hex;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  // Perceived luminance — weights match WCAG / sRGB
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luma > 0.6 ? '#1f2937' : '#ffffff';
}

const LevelBadge = ({
  color,
  textColor,
  label,
  size = 'md',
  className = '',
}: LevelBadgeProps) => {
  const bg = color || 'var(--color-status-neutral)';
  const fg = textColor || getContrastingTextColor(color);
  return (
    <span
      className={`${styles.badge} ${size === 'sm' ? styles.sm : ''} ${className}`.trim()}
      style={{ background: bg, color: fg }}
    >
      {label}
    </span>
  );
};

export default LevelBadge;
